import {
  AttentionBlock,
  Button,
  ListRow,
  ListRows,
  StatusTag,
  formatDate,
  plural,
  titleCase,
} from '@westhetics/ui'
import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { routes } from '@/routes'
import type { Patient, PendingItem } from './api'
import { MATCH_KEY_LABELS, completenessSentence } from './labels'

/**
 * O topo da ficha, acima das abas (docs/areas/pacientes.md, "Cabeçalho e título"): o medidor de
 * cadastro completo com a consequência escrita (RN-PAC-09), as pendências por extenso (RN-PAC-10)
 * e a sugestão de duplicados (RN-PAC-16). Cada linha leva ao lugar de resolver.
 */

type PatientTab = 'details' | 'anamneses' | 'documents' | 'treatment-plans'

const PENDING_TABS: Record<PendingItem['target']['type'], PatientTab> = {
  appointment: 'documents',
  anamnesis: 'anamneses',
  treatment_plan: 'treatment-plans',
}

export function PatientSummary({
  patient,
  canManage,
  isDesk,
  activeTab,
  restore,
}: {
  patient: Patient
  canManage: boolean
  isDesk: boolean
  activeTab: PatientTab
  /** A ação de restaurar, para o aviso do cadastro arquivado (gestora e root). */
  restore: ReactNode
}) {
  const navigate = useNavigate()
  const { completeness, pendingItems, duplicates } = patient
  const isArchived = patient.status === 'archived'
  const isAnonymized = Boolean(patient.anonymizedAt)
  const [firstMissing, ...otherMissing] = completeness.missing
  const hasPending = pendingItems.items.length > 0

  return (
    <div className="mt-5 flex flex-col gap-4">
      {isAnonymized ? (
        <AttentionBlock variant="lilac" title="Cadastro anonimizado a pedido da paciente">
          Os dados que identificam a pessoa foram apagados. As anamneses respondidas, os documentos
          e os planos continuam guardados como prova. Este cadastro não pode ser restaurado.
        </AttentionBlock>
      ) : isArchived ? (
        <AttentionBlock variant="lilac" title="Este cadastro está arquivado">
          Não aparece nas listas, na busca nem nas escolhas novas. As anamneses, os documentos e os
          planos continuam guardados
          {patient.archivedAt ? `, desde ${formatDate(new Date(patient.archivedAt))}` : ''}.
          {restore ? <div className="mt-3">{restore}</div> : ' Peça a uma gestora para restaurar.'}
        </AttentionBlock>
      ) : null}

      {patient.sourceClinic ? (
        <AttentionBlock variant="lilac" title={`Cadastro de ${patient.sourceClinic.name}`}>
          Você lê esta ficha porque as clínicas são da mesma rede. Quem tem acesso a{' '}
          {patient.sourceClinic.name} é quem edita.
        </AttentionBlock>
      ) : null}

      {isArchived || isAnonymized ? null : (
        <div className={hasPending && firstMissing ? 'desk:grid-cols-2 grid gap-4' : undefined}>
          {firstMissing ? (
            // Plum só quando o que falta impede o contrato; faltando só o e-mail, é contexto.
            <AttentionBlock
              variant={completeness.isReadyForContract ? 'lilac' : 'plum'}
              title={completenessSentence(completeness)}
            >
              {firstMissing.consequence}
              {otherMissing.map((item) => (
                <div key={item.field}>
                  · {item.label}: {item.consequence}
                </div>
              ))}
              {/* No celular a edição não existe: a saída é a ficha num computador (RN-PAC-24). */}
              {patient.isReadOnly || activeTab === 'details' ? null : (
                <div className="mt-2">
                  <Button variant="text" asChild>
                    <Link to={routes.patientTab(patient.id, 'details')}>
                      {isDesk ? 'Completar cadastro' : 'continuar no computador'}
                    </Link>
                  </Button>
                </div>
              )}
            </AttentionBlock>
          ) : hasPending ? null : (
            <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
              Cadastro 100% completo. O contrato pode ser gerado.
            </p>
          )}

          {hasPending ? (
            <section aria-label="Pendências">
              <ListRows>
                {pendingItems.items.map((item) => (
                  <ListRow
                    key={`${item.type}:${item.target.id}`}
                    critical={item.level === 'critical'}
                    title={item.text}
                    trailing={<StatusTag level={item.level}>{item.label}</StatusTag>}
                    onClick={() =>
                      navigate(routes.patientTab(patient.id, PENDING_TABS[item.target.type]))
                    }
                  />
                ))}
              </ListRows>
              {firstMissing ? null : (
                <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6] font-light">
                  Cadastro 100% completo.
                </p>
              )}
            </section>
          ) : null}
        </div>
      )}

      {duplicates.total > 1 ? (
        <AttentionBlock
          variant="lilac"
          title={`${plural(duplicates.total, 'cadastro duplicado', 'cadastros duplicados')}`}
        >
          {duplicates.patients.map((other) => (
            <div key={`${other.id}:${other.matchKey}`}>
              · {titleCase(other.name)} · {MATCH_KEY_LABELS[other.matchKey]}
            </div>
          ))}
          <div className="mt-2">
            {canManage && !patient.isReadOnly ? (
              <Button variant="text" asChild>
                <Link to={routes.patientDuplicates(patient.id)}>Unificar cadastros</Link>
              </Button>
            ) : (
              'Avise a gestora: só ela unifica cadastros.'
            )}
          </div>
        </AttentionBlock>
      ) : null}
    </div>
  )
}
